import { InteractionRequiredAuthError, type AuthenticationResult } from '@azure/msal-browser';
import { app as teamsApp, authentication as teamsAuth } from '@microsoft/teams-js';
import { authConfigured, loginScopes, msalInstance } from './msal.js';

/** Outlook Add-in(office.js) 컨텍스트 여부 */
export function isOfficeContext(): boolean {
  return (
    typeof Office !== 'undefined' &&
    Boolean(Office?.context?.host) &&
    typeof OfficeRuntime !== 'undefined'
  );
}

/**
 * 임베드(iframe) 컨텍스트 여부 — Teams/Outlook 개인 탭 등.
 * 이 경우 Microsoft 로그인 페이지는 frame-ancestors 제한으로 프레임 안에서 못 열리므로
 * loginRedirect 대신 loginPopup(최상위 팝업 창)을 써야 한다.
 */
export function isEmbedded(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    return true; // 크로스 오리진 접근 예외 자체가 "프레임 안"을 의미
  }
}

// ── Teams SSO (Teams/Outlook 임베드 personal tab에서 무팝업 토큰) ──────────────
let teamsInit: Promise<boolean> | null = null;
/** Teams/Outlook 호스트 컨텍스트에서만 초기화 성공. 일반 브라우저는 시도 안 함(초기화 hang 방지). */
function ensureTeams(): Promise<boolean> {
  if (teamsInit) return teamsInit;
  teamsInit = (async () => {
    if (!isEmbedded()) return false;
    try {
      await Promise.race([
        teamsApp.initialize(),
        new Promise<never>((_, reject) => setTimeout(() => reject(new Error('teams init timeout')), 2500)),
      ]);
      return true;
    } catch {
      return false;
    }
  })();
  return teamsInit;
}

/**
 * Teams SSO 무팝업 토큰. aud = api://{host}/{client-id}(매니페스트 webApplicationInfo.resource).
 * Entra의 API 노출 + Office/Teams 클라이언트 사전승인이 끝나야 동작하며, 그 전에는 null → MSAL 폴백.
 */
async function getTeamsSsoToken(): Promise<string | null> {
  try {
    if (!(await ensureTeams())) return null;
    const token = await teamsAuth.getAuthToken();
    return token || null;
  } catch (err) {
    console.warn('Teams SSO 실패 — MSAL 폴백', err);
    return null;
  }
}

/** 상호작용 로그인 — 임베드면 popup, 일반 브라우저면 redirect. popup 성공 시 결과 반환. */
async function interactiveLogin(): Promise<AuthenticationResult | null> {
  if (isEmbedded()) {
    const res = await msalInstance.loginPopup({ scopes: loginScopes });
    if (res?.account) msalInstance.setActiveAccount(res.account);
    return res;
  }
  await msalInstance.loginRedirect({ scopes: loginScopes });
  return null; // redirect 진행 → 페이지 이탈
}

let initialized = false;
async function ensureInit(): Promise<void> {
  if (initialized) return;
  await msalInstance.initialize();
  await msalInstance.handleRedirectPromise();
  initialized = true;
}

/**
 * 앱 진입 시 로그인 보장.
 * - Outlook Add-in: Office SSO 가 처리하므로 통과(토큰은 호출 시 획득).
 * - 브라우저: ssoSilent 우선 → 실패 시 loginRedirect.
 * @returns true=로그인됨 / false=리다이렉트 진행 중(렌더 보류)
 */
export async function ensureSignedIn(): Promise<boolean> {
  if (!authConfigured) return true; // 개발 모킹 모드

  // MSAL 계정을 진입 시 확보(이후 데이터 호출은 silent). 계정 없으면 ssoSilent → 실패 시 로그인 카드.
  await ensureInit();
  if (msalInstance.getAllAccounts().length > 0) return true;
  try {
    const res = await msalInstance.ssoSilent({ scopes: loginScopes });
    if (res?.account) {
      msalInstance.setActiveAccount(res.account);
      return true;
    }
  } catch (err) {
    if (!(err instanceof InteractionRequiredAuthError)) {
      console.warn('ssoSilent 실패 — 수동 로그인 필요');
    }
  }
  return false; // 로그인 화면(버튼) 노출 → 클릭 시 popup(임베드)/redirect 로 계정 확보
}

/**
 * 상호작용 로그인. 로그인 카드 버튼에서 호출.
 * @returns true=로그인 완료(popup) / false=리다이렉트 진행(페이지 이탈) 또는 미구성
 *   → 호출부는 true일 때 화면을 앱으로 전환해야 한다(popup은 리로드가 없으므로).
 */
export async function signIn(): Promise<boolean> {
  if (!authConfigured) return false;
  await ensureInit();
  try {
    const res = await interactiveLogin();
    return Boolean(res?.account);
  } catch (err) {
    console.warn('로그인 실패', err);
    return false;
  }
}

/**
 * 백엔드 호출용 토큰.
 * - Office 컨텍스트: Office SSO 부트스트랩 토큰(getAccessToken).
 * - 브라우저: silent 로 idToken 갱신 후 반환(aud=client-id → 백엔드 검증).
 */
export async function acquireApiToken(): Promise<string | null> {
  if (!authConfigured) return null; // 모킹 모드: 토큰 없이 호출

  await ensureInit();
  const account = msalInstance.getAllAccounts()[0];

  // 계정 있음 → MSAL silent idToken(aud=client-id) 우선. 가장 흔한 경로.
  if (account) {
    try {
      let res = await msalInstance.acquireTokenSilent({ scopes: loginScopes, account });
      // acquireTokenSilent 은 access token 만 갱신하고 캐시된(만료된) idToken 을 줄 수 있음.
      // idToken exp 가 임박/만료면 강제 갱신해 새 idToken 확보(백엔드 JWTExpired 방지).
      const expMs = ((res.idTokenClaims as { exp?: number } | undefined)?.exp ?? 0) * 1000;
      if (expMs < Date.now() + 60_000) {
        res = await msalInstance.acquireTokenSilent({ scopes: loginScopes, account, forceRefresh: true });
      }
      return res.idToken;
    } catch (err) {
      if (err instanceof InteractionRequiredAuthError && isEmbedded()) {
        try {
          const res = await msalInstance.acquireTokenPopup({ scopes: loginScopes, account });
          return res.idToken;
        } catch {
          return null;
        }
      }
      if (err instanceof InteractionRequiredAuthError) await msalInstance.loginRedirect({ scopes: loginScopes });
      return null;
    }
  }

  // 계정 없음 → 무팝업 SSO(Teams) 시도 후 상호작용 로그인.
  // (Office SSO getAccessToken 은 Entra "API 노출" 미설정 시 오류 대화상자를 띄우므로 호출하지 않음)
  if (isEmbedded()) {
    const teamsToken = await getTeamsSsoToken();
    if (teamsToken) return teamsToken;
    try {
      const r = await msalInstance.loginPopup({ scopes: loginScopes });
      if (r?.account) msalInstance.setActiveAccount(r.account);
      return r?.idToken ?? null;
    } catch {
      return null;
    }
  }
  await msalInstance.loginRedirect({ scopes: loginScopes });
  return null;
}
